import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';

import { HowItWorksSteps, getPromotionStepContents } from '@/components/HowItWorksSteps';
import { LineSeparator } from '@/components/LineSeparator';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Box } from '@/components/lib/components';
import { URLS } from '@/data';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { Share } from '@/feature/entry-share/utils/share';
import { RafPageContent } from '@/feature/invite-friends/components/RafPageContent';
import { TermsAndConditions } from '@/feature/invite-friends/components/TermsAndConditions';
import { useRaf } from '@/feature/invite-friends/hooks/use-raf';
import { PromoDetailsPage } from '@/feature/promohub/components/PromoPage';
import { DEFAULT_PROMO_THEME } from '@/feature/promohub/hooks/use-promo-theme';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { InviteFriendsStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { isFilled } from '@prismicio/client';

import { useSendInvite } from '../components/SendInviteProvider';

type InviteFriendsRouteProp = RouteProp<InviteFriendsStackParamList, 'InviteFriends'>;

export const InviteFriendsScreen = () => {
    const navigation = useNavigation();
    const { params } = useRoute<InviteFriendsRouteProp>();
    const { t } = useTranslation(['raf', 'promohub']);
    const { showInfoSheet } = useAlerts();
    const { data, isPending } = useRaf();
    const rafContent = data?.data;
    const theme = rafContent?.theme.data ?? DEFAULT_PROMO_THEME;
    const steps = rafContent?.sender_steps;
    const howItWorksSteps = getPromotionStepContents(steps);
    const tcLink = rafContent?.tc_link;
    const buttonCta =
        rafContent && rafContent?.sender_button_label ? rafContent?.sender_button_label : t('send_invite');

    const profile = useUserStore(state => state.profile);
    const { showSendInviteModal } = useSendInvite();
    const userId = profile.sub;
    const activeProduct = useJurisdictionStore(state => state.product);

    const onSendInvite = async () => {
        const url = `${URLS.PROMO_RAF_PAGE_URL}/referafriend/${activeProduct}?userId=${userId}`;
        if (isWeb) {
            showSendInviteModal({
                link: url,
            });
        } else {
            await Share.open({ url });
        }
    };

    const handleInfoIconPress = () => {
        showInfoSheet({
            title: t('how_it_works'),
            description: (
                <Box paddingTop={'s24'}>
                    {howItWorksSteps ? <HowItWorksSteps steps={howItWorksSteps} /> : null}
                    <LineSeparator />
                    {isFilled.link(tcLink) && !!tcLink.url ? (
                        <TermsAndConditions link={tcLink.url} info={rafContent?.sender_details_text} />
                    ) : null}
                </Box>
            ),
            buttonLabel: t('got_it'),
        });
    };

    const handleHistoryIconPress = () => {
        navigation.navigate('ReferralHistory');
    };

    if (isPending) {
        return (
            <View style={[common.flex, common.alignCenter, common.justifyCenter]}>
                <LoadingSpinner />
            </View>
        );
    }

    return (
        <Box flex={1}>
            <PromoDetailsPage
                theme={theme}
                pageContent={<RafPageContent data={data} />}
                headerProps={{
                    title: rafContent?.sender_title,
                    onPress: onSendInvite,
                    isOptedIn: false,
                    buttonCta,
                    showButton: true,
                    buttonTestID: 'refer-a-friend-invite-button',
                    icon: rafContent?.sender_image?.url ?? '',
                    refferalSummaryCard: null, // add const { promo_title_color } = usePromoTheme(theme); <ReferralSummaryCard color={promo_title_color} /> once RAF gets added to BE also
                    showNavigationButton: !params?.hideNavigationButton,
                    showRafHistory: false,
                    infoIconPress: handleInfoIconPress,
                    historyIconPress: handleHistoryIconPress,
                }}
            />
        </Box>
    );
};
